import { ErrorPageDex, EmptyPageDex } from '../shared/Common';
import { useWalletsSearchFilters } from './usePageParams';
import { GetWalletsParamsV2 } from 'api/types/httpsTypes/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { duration } from 'moment';
import Header from 'modules/shared-components/data-display/table/header';
import Table from 'modules/shared-components/data-display/table';
import { desktopColumns, mobileColumns } from './columns';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { WalletsFiltersBar } from './WalletsFilters';
import { TableRow } from './TableRow';
import { useRef } from 'react';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { useResearchSettings } from 'modules/routing/mainSettingsProvider';
import { twMerge } from 'tailwind-merge';

function thDynamicClass(s: string) {
  return s === 'UNIQUE_TOKENS' || s === 'ADDRESS' ? 'col-span-2' : '';
}

export function Wallets() {
  const listRef = useRef<HTMLTableElement>(null);
  const { filtersState, setSort } = useWalletsSearchFilters();
  const [selectedChain] = useGlobalChain();
  const isSortAscending = !filtersState.sort?.includes('-');
  const { researchSettings } = useResearchSettings();

  const params: GetWalletsParamsV2 = {
    sort: filtersState.sort,
    address: filtersState.addressFilter,
    chain: selectedChain,
    period: filtersState.period,
    minBalance: filtersState.minBalance,
    maxBalance: filtersState.maxBalance,
    minPnl: filtersState.minPnl,
    maxPnl: filtersState.maxPnl,
    tradingStyle: filtersState.tradingStyle,
    isChainAsset: filtersState.isChainAsset,
  };

  const { data, error, isLoading } = useQuery({
    queryKey: contractServiceKeys.getWalletsV2(params),
    queryFn: () => ContractService.getWalletsV2(params),
    keepPreviousData: true,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });

  const isLargeScreen = useMediaQuery(screenGte.large);

  const columns = isLargeScreen
    ? desktopColumns(filtersState.period, !!filtersState.isChainAsset)
    : researchSettings.isTableExpanded
    ? desktopColumns(filtersState.period, !!filtersState.isChainAsset)
    : mobileColumns(filtersState.period, !!filtersState.isChainAsset);

  return (
    <div className="space-y-2 lg:px-2">
      <div className="xxs:px-1 lg:px-0">
        <WalletsFiltersBar />
      </div>

      {error ? (
        <ErrorPageDex />
      ) : (
        <div className="overflow-x-auto">
          <div className="w-full  xxs:my-3 lg:min-w-[1300px] ">
            {!isLargeScreen && <div ref={listRef} />}
            <Table
              className={twMerge(
                // 🧱 Base table padding styles
                'xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 ' +
                  'lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6',

                // 🧲 Conditional sticky first column (mobile expanded view)
                !isLargeScreen &&
                  researchSettings.isTableExpanded &&
                  [
                    // Sticky behavior
                    'xxs:[&_td:first-of-type]:sticky xxs:[&_td:first-of-type]:left-0',
                    'xxs:[&_th:first-of-type]:sticky xxs:[&_th:first-of-type]:left-0',
                    'xxs:[&_td:first-of-type]:z-[5] xxs:[&_th:first-of-type]:z-[5]',
                    'xxs:[&_td:first-of-type]:bg-dex-black-900 xxs:[&_th:first-of-type]:bg-dex-black-900',
                    'xxs:[&_td:first-of-type]:shadow-[4px_0_4px_-2px_rgba(0,0,0,0.4)]',
                  ].join(' ')
              )}
            >
              <Header
                trClassName="lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-900  border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700"
                columns={columns}
                thDynamicClass={thDynamicClass}
                rowClassName="xxs:p-1"
                isSortAscending={isSortAscending}
                sortBy={filtersState.sort?.replace('-', '')}
                className="xxs:dark:bg-dex-black-900 xxs:text-xs lg:grid"
                onClickColumn={(_, sortableKey) => {
                  if (sortableKey && !isLoading) setSort(sortableKey);
                }}
              />
              <tbody className="lg:block lg:h-terminal overflow-y-auto">
                {isLoading ? (
                  [...Array(10).keys()]
                    .map((i) => i + 1)
                    .map((index) => (
                      <Table.LoadingRow
                        thDynamicClass={thDynamicClass}
                        key={index}
                        elementClassName="xxs:dark:bg-dex-black-700"
                        className="lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-900"
                        columns={columns}
                      />
                    ))
                ) : data?.length === 0 ? (
                  <EmptyPageDex height="100px" title="No Wallets Found" />
                ) : (
                  data?.map((item) => (
                    <TableRow
                      key={item.address + '_' + item.chain}
                      data={item}
                      columnsDef={columns}
                    />
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
